import { create, windowScheduler, indexedResolver } from "@yornaath/batshit";
import {
  SecurityAppAssets,
  SecurityAppAssetsCategories,
  SecurityAppAssetsPriorities,
  SecurityAppAssetsSources,
  SecurityAppAssetsSourcesImportBackends,
  SecurityAppAssetsVulnerabilityScanners
} from "@graylog/enterprise-api";
import { qualifyUrl } from "util/URLUtils";
import EnterpriseApiRoutes from "common/ApiRoutes";
import glFetch from "logic/rest/FetchProvider";
import { stringifyFilters } from "security-app/components/common/Filters/stringifyFilters";
import {
  parseFilterValuesParamsToURLString,
  parseSearchParamsToURLString,
  parseSliceParamsToURLString
} from "security-app/components/common/EntityTableHelpers";
import { assetsQueryKey } from "security-app/hooks/queryKeys";
export const fetchAssetLabels = async (page, perPage, query = void 0) => {
  const params = [`page=${page}`, `per_page=${perPage}`];
  if (query) params.push(`query=${encodeURI(query)}`);
  return glFetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.assetLabels(params.join("&")).url)).then(
    (response) => {
      var _a;
      return {
        ...response,
        assets: (_a = response.assets) == null ? void 0 : _a.map((asset) => ({ id: asset._id, name: asset.name }))
      };
    }
  );
};
export const searchAssetsKeyFC = (searchParams) => {
  if (!searchParams) return assetsQueryKey("search");
  return assetsQueryKey(
    "search",
    parseSearchParamsToURLString(searchParams),
    searchParams.sliceCol,
    searchParams.slice
  );
};
export const searchAssets = async (searchParams) => {
  const params = parseSearchParamsToURLString(searchParams);
  return glFetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.paginated(params).url));
};
export const fetchAssetSlices = async (sliceColumn, query, filters) => {
  const params = parseSliceParamsToURLString(sliceColumn, query, filters);
  return glFetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.slices(params).url));
};
export const fetchAssetFilterValues = async (field, searchParams, query, filters) => {
  const params = parseFilterValuesParamsToURLString(field, searchParams, query, filters);
  return glFetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.filterValues(params).url));
};
export const fetchAsset = async (assetId) => glFetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.singleAsset(assetId).url));
const byIdsFetcher = (assetIds) => glFetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.assetByIds().url), {
  asset_ids: assetIds
});
const batcherAssetByIds = create({
  fetcher: byIdsFetcher,
  resolver: indexedResolver(),
  scheduler: windowScheduler(10)
});
export const getAssetById = async (assetId) => batcherAssetByIds.fetch(assetId);
export const deleteAsset = async ({ assetId }) => SecurityAppAssets.deleteAsset(assetId);
export const updateAsset = async ({ assetId, asset }) => glFetch("PUT", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.singleAsset(assetId).url), asset);
export const createAsset = async ({ asset }) => glFetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.new().url), asset);
export const fetchPriorities = async () => SecurityAppAssetsPriorities.listPriorities("priority", 1, 1e4, void 0, "desc");
export const fetchCategories = async () => glFetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.categories.index().url));
export const addPriority = async ({ priority }) => SecurityAppAssetsPriorities.create(priority);
export const reprioritizePriorities = async ({ payload }) => SecurityAppAssetsPriorities.updatePriorities(payload);
export const updatePriority = async ({ priorityId, priority }) => SecurityAppAssetsPriorities.update(priorityId, priority);
export const deletePriority = async ({ priorityId }) => SecurityAppAssetsPriorities.remove(priorityId);
export const addCategory = async ({ category }) => SecurityAppAssetsCategories.create(category);
export const updateCategory = async ({ categoryId, category }) => SecurityAppAssetsCategories.update(categoryId, category);
export const deleteCategory = async ({ categoryId }) => SecurityAppAssetsCategories.remove(categoryId);
export const bulkAddCategories = async ({ assetIds, categories }) => SecurityAppAssets.addEvidence({
  asset_ids: assetIds,
  categories
});
export const fetchAssetSources = async (page, perPage, query = null, orderBy = null, direction = null, filters = {}) => {
  const params = [`page=${page}`, `per_page=${perPage}`];
  if (query) params.push(`query=${encodeURI(query)}`);
  if (filters && Object.keys(filters).length > 0) {
    params.push(`filters=${encodeURI(stringifyFilters(filters))}`);
  }
  if (orderBy) {
    params.push(`sort=${orderBy}`);
  }
  if (direction) {
    params.push(`direction=${direction}`);
  }
  return glFetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.sources.index(params.join("&")).url));
};
export const fetchAssetSource = async (sourceId) => glFetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.sources.details(sourceId).url));
export const createAssetSource = async ({ source }) => glFetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.sources.new().url), source);
export const updateAssetSource = async ({
  sourceId,
  source
}) => glFetch("PUT", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.sources.update(sourceId).url), source);
export const deleteAssetSource = async ({ sourceId }) => SecurityAppAssetsSources.remove(sourceId);
export const fetchMS365DeploymentTypes = async () => glFetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.sources.ms365DeploymentTypes().url));
export const fetchAssetSourceMappings = async (sourceId, page, perPage) => {
  const params = [`page=${page}`, `per_page=${perPage}`, "sort=updated_at", "direction=desc"];
  return glFetch(
    "GET",
    qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.sources.mappings.index(sourceId, params.join("&")).url)
  );
};
export const fetchAssetSourceMapping = async (sourceId, mappingId) => glFetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.sources.mappings.details(sourceId, mappingId).url));
export const createAssetSourceMapping = async ({
  sourceId,
  mapping
}) => new Promise((resolve, reject) => {
  fetch(qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.sources.mappings.new(sourceId).url), {
    method: "POST",
    body: JSON.stringify(mapping),
    headers: {
      "Content-Type": "application/json",
      "X-Requested-By": "XMLHttpRequest"
    }
  }).then(async (response) => {
    const respBody = await response.json();
    if (response.ok) resolve(respBody);
    else reject(respBody);
  });
});
export const updateAssetSourceMapping = async ({
  sourceId,
  mappingId,
  mapping
}) => new Promise((resolve, reject) => {
  fetch(qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.sources.mappings.update(sourceId, mappingId).url), {
    method: "PUT",
    body: JSON.stringify(mapping),
    headers: {
      "Content-Type": "application/json",
      "X-Requested-By": "XMLHttpRequest"
    }
  }).then(async (response) => {
    const respBody = await response.json();
    if (response.ok) resolve(respBody);
    else reject(respBody);
  });
});
export const deleteAssetSourceMapping = async ({ sourceId, mappingId }) => SecurityAppAssetsSourcesImportBackends.remove(sourceId, mappingId);
export const importAssets = async (sourceId, mappingId) => glFetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.sources.mappings.import(sourceId, mappingId).url));
export const testAssetSource = async ({ source }) => glFetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.sources.testNew().url), source);
export const testNewAssetSourceMapping = async ({
  sourceId,
  mapping
}) => {
  const mappingId = mapping.id || "new";
  const payload = { asset_import_backend_configuration: mapping };
  return glFetch(
    "POST",
    qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.sources.mappings.testNew(sourceId, mappingId).url),
    payload
  );
};
export const bulkTestAssetSource = async ({ ids }) => {
  const requestBody = {
    ids
  };
  return SecurityAppAssetsSources.bulkTestConnections(requestBody);
};
export const testSavedAssetSourceMapping = async (sourceId, mappingId) => glFetch(
  "GET",
  qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.sources.mappings.testSaved(sourceId, mappingId).url)
);
export const toggleMappingSync = async ({
  sourceId,
  mappingId,
  enabled,
  interval_hours
}) => {
  const requestBody = {
    enabled: !enabled,
    interval_hours
  };
  return SecurityAppAssetsSourcesImportBackends.scheduleImport(sourceId, mappingId, requestBody);
};
export const fetchScanners = async (page, perPage, query, orderBy = "title", direction = "asc", filters = {}) => {
  const params = [`page=${page}`, `per_page=${perPage}`];
  if (query) params.push(`query=${encodeURI(query)}`);
  if (orderBy) params.push(`sort=${orderBy}`);
  if (direction) params.push(`direction=${direction}`);
  if (filters && Object.keys(filters).length > 0) {
    params.push(`filters=${encodeURI(stringifyFilters(filters))}`);
  }
  return glFetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.scanners.index(params.join("&")).url));
};
export const addScanner = async ({ scanner }) => glFetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.scanners.new().url), scanner);
export const deleteScanner = async ({ scannerId }) => SecurityAppAssetsVulnerabilityScanners.remove(scannerId);
export const editScanner = async ({ scanner }) => glFetch("PUT", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.scanners.edit(scanner.id).url), scanner);
export const toggleScannerSync = async ({
  scannerId,
  enabled,
  interval_hours
}) => {
  const requestBody = {
    enabled: !enabled,
    interval_hours
  };
  return SecurityAppAssetsVulnerabilityScanners.scheduleImport(scannerId, requestBody);
};
export const testNewScannerConnection = async ({ scanner }) => glFetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.scanners.testNewScannerConnection().url), scanner);
export const testScannerConnection = async ({ id }) => SecurityAppAssetsVulnerabilityScanners.testConnection(id);
export const importVulnerabilityScans = async (scannerId) => glFetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Assets.scanners.import(scannerId).url));
